import { ReactNode } from "react";
import { Icon, IconGlyph } from "./Icon";
import { Role } from "../../types/entities";

export function Eyebrow({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 ${className}`}>
      {children}
    </div>
  );
}

export function Hairline({ className = "" }: { className?: string }) {
  return <div className={`h-px bg-line ${className}`} />;
}

export function Field({
  label,
  hint,
  children,
  /**
   * Id of the control this labels. Renders the caption as a real <label>
   * instead of a plain div, which both screen readers and browser autofill
   * heuristics rely on to work out what a field is for.
   */
  htmlFor,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  const caption = `[ ${label} ]`;
  return (
    <div>
      {htmlFor ? (
        <label
          htmlFor={htmlFor}
          className="block font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 mb-2"
        >
          {caption}
        </label>
      ) : (
        <Eyebrow className="mb-2">{caption}</Eyebrow>
      )}
      {children}
      {hint && (
        <div className="font-mono text-[9.5px] tracking-[1.2px] text-ink3 mt-1.5 uppercase">
          {hint}
        </div>
      )}
    </div>
  );
}

export function PrimaryButton({
  children,
  meta,
  tone,
  type = "button",
  onClick,
  disabled,
}: {
  children: ReactNode;
  meta?: ReactNode;
  tone?: "ink" | "sage" | "clay";
  type?: "button" | "submit";
  onClick?: () => void;
  disabled?: boolean;
}) {
  const bg =
    tone === "sage" ? "bg-vote-yes" : tone === "clay" ? "bg-clay" : "bg-ink";
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`w-full border-0 py-4 px-[18px] rounded-lg ${bg} text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-between gap-3 disabled:opacity-40`}
    >
      <span>{children}</span>
      {meta && (
        <span className="font-mono text-[10.5px] opacity-65 font-medium tracking-wide flex items-center gap-1.5">
          {meta}
        </span>
      )}
    </button>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card border border-line-soft rounded-lg overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

const ROLE_META: Record<Role | "medlem", { label: string; icon: IconGlyph; className: string }> = {
  ADMIN: { label: "Admin", icon: "sliders", className: "text-bilberry border-bilberry" },
  TAX_COLLECTOR: { label: "Bødeminister", icon: "coins", className: "text-clay border-clay" },
  medlem: { label: "Medlem", icon: "user", className: "text-ink3 border-ink3" },
};

export function RoleBadge({ role, size = "text-[9.5px]" }: { role: Role | "medlem"; size?: string }) {
  const meta = ROLE_META[role];
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono ${size} tracking-[1.3px] font-semibold uppercase border rounded px-1.5 py-1 ${meta.className}`}
    >
      <Icon name={meta.icon} size={9.5} />
      {meta.label}
    </span>
  );
}

export function AdminHeader({
  back = "Admin",
  title,
  sub,
  action,
  onBack,
}: {
  back?: string;
  title: ReactNode;
  sub?: string;
  action?: ReactNode;
  onBack?: () => void;
}) {
  return (
    <div className="px-7 pt-2">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 flex items-center gap-1.5"
        >
          <Icon name="chevron-left" size={12} />
          <span>{back}</span>
        </button>
        {action}
      </div>
      {sub && <Eyebrow className="mt-4">{sub}</Eyebrow>}
      <div className="font-display text-[40px] leading-[0.9] tracking-tight font-extrabold mt-1.5 uppercase">
        {title}
      </div>
    </div>
  );
}

export function AdminRow({
  icon,
  label,
  meta,
  tone,
  chevron = true,
  onClick,
}: {
  icon: IconGlyph;
  label: string;
  meta?: string;
  tone?: string;
  chevron?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 px-3.5 py-3.5 border-t border-line-soft first:border-t-0 w-full text-left"
    >
      <div
        className={`w-[30px] h-[30px] rounded flex-shrink-0 bg-paper2 flex items-center justify-center ${tone ?? "text-ink2"}`}
      >
        <Icon name={icon} size={13} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-[14.5px] font-bold uppercase tracking-tight">{label}</div>
        {meta && (
          <div className="font-mono text-[9.5px] tracking-[1.2px] text-ink3 mt-1 uppercase">{meta}</div>
        )}
      </div>
      {chevron && <Icon name="chevron-right" size={10} className="text-ink3" />}
    </button>
  );
}
